import React, { useMemo, useState } from 'react';
import T from 'prop-types';
import { Box } from '@mui/material';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import geoData from '../../assets/files/countries.geojson.json';

const WorldMapChart = ({
  defaultCountryColor,
  defaultCountryHoverColor,
  hoverColor,
  selectedFillColor,
  popupTemplate,
  data,
  projectionConfig,
  width,
  height,
}) => {
  const [tooltip, setTooltip] = useState(null);
  // tooltip = { geo, countryData, x, y }

  /**
   * Transform API data into lookup by ISO code
   */
  const choroplethData = useMemo(() => {
    return data.reduce((acc, item) => {
      acc[item.isoCode] = item;
      return acc;
    }, {});
  }, [data]);

  const updateTooltipPosition = (evt, geo, countryData) => {
    const { clientX, clientY } = evt;

    setTooltip({
      geo,
      countryData,
      x: clientX + 10,
      y: clientY + 10,
    });
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
      }}
    >
      <ComposableMap
        projection="geoEqualEarth"
        width={width}
        height={height}
        style={{
          width: '100%',
          height: '100%',
        }}
        preserveAspectRatio="xMidYMid meet"
        projectionConfig={projectionConfig}
      >
        <Geographies geography={geoData}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const isoA3 = geo.properties['ISO3166-1-Alpha-3'];
              const countryData = choroplethData[isoA3];

              const fill = countryData ? selectedFillColor : defaultCountryColor;

              const hover = countryData ? hoverColor : defaultCountryHoverColor;

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={fill}
                  stroke="#FFF"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: 'none' },
                    hover: {
                      fill: hover,
                      outline: 'none',
                    },
                    pressed: { outline: 'none' },
                  }}
                  onMouseEnter={(evt) => {
                    if (!popupTemplate || !countryData) return;

                    updateTooltipPosition(evt, geo, countryData);
                  }}
                  onMouseMove={(evt) => {
                    if (!tooltip) return;

                    updateTooltipPosition(evt, geo, countryData);
                  }}
                  onMouseLeave={() => {
                    setTooltip(null);
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {tooltip && popupTemplate && (
        <Box
          sx={{
            position: 'fixed',
            top: tooltip.y,
            left: tooltip.x,
            transform: 'translate(0, 0)',
            pointerEvents: 'none',
            zIndex: 9999,
            whiteSpace: 'nowrap',
          }}
        >
          {popupTemplate(tooltip.geo, tooltip.countryData)}
        </Box>
      )}
    </Box>
  );
};

WorldMapChart.defaultProps = {
  popupTemplate: () => null,
  data: [],
};

WorldMapChart.propTypes = {
  defaultCountryColor: T.string.isRequired,
  defaultCountryHoverColor: T.string,
  selectedFillColor: T.string,
  hoverColor: T.string.isRequired,
  popupTemplate: T.func,
  data: T.array,
  projectionConfig: T.shape({
    scale: T.number,
    center: T.array,
  }),
  width: T.number,
  height: T.number,
};

export default React.memo(WorldMapChart);
